// SEO Configuration - All values pulled from environment variables
// This centralizes all SEO settings for easy management

const SEO_CONFIG = {
  // Site Information
  siteName: process.env.REACT_APP_SITE_NAME || 'ClawStinger',
  siteUrl: process.env.REACT_APP_SITE_URL || 'https://clawstinger.com',
  siteDescription: process.env.REACT_APP_SITE_DESCRIPTION || 'Deploy OpenClaw AI agents in under 60 seconds. Managed hosting for Claude, GPT, and AI assistants. No SSH, no DevOps, no hassle.',
  
  // Keywords for SEO
  defaultKeywords: process.env.REACT_APP_SEO_KEYWORDS || 'openclaw, simpleclaw, myclaw, claude bot, claude assistant, AI hosting, managed AI, AI agent hosting, openai hosting, claude hosting, telegram bot hosting, discord bot hosting, AI assistant deployment, self-hosted AI, claude opus hosting, AI VPS, managed claude, anthropic claude hosting',
  
  // Social Media
  twitterHandle: process.env.REACT_APP_TWITTER_HANDLE || '@clawstinger',
  facebookAppId: process.env.REACT_APP_FACEBOOK_APP_ID || '',
  
  // Analytics & Verification
  googleAnalyticsId: process.env.REACT_APP_GA_MEASUREMENT_ID || '',
  googleSiteVerification: process.env.REACT_APP_GOOGLE_SITE_VERIFICATION || '',
  bingSiteVerification: process.env.REACT_APP_BING_SITE_VERIFICATION || '',
  yandexVerification: process.env.REACT_APP_YANDEX_VERIFICATION || '',
  
  // Organization Info (for Schema.org)
  organization: {
    name: process.env.REACT_APP_ORG_NAME || 'ClawStinger',
    logo: process.env.REACT_APP_ORG_LOGO || '/logo512.png',
    email: process.env.REACT_APP_ORG_EMAIL || 'support@clawstinger.com',
    phone: process.env.REACT_APP_ORG_PHONE || '',
    address: {
      street: process.env.REACT_APP_ORG_STREET || '',
      city: process.env.REACT_APP_ORG_CITY || '',
      state: process.env.REACT_APP_ORG_STATE || '',
      zip: process.env.REACT_APP_ORG_ZIP || '',
      country: process.env.REACT_APP_ORG_COUNTRY || 'US',
    },
  },
  
  // Social Links (for Schema.org)
  socialLinks: [
    process.env.REACT_APP_SOCIAL_TWITTER || 'https://twitter.com/clawstinger',
    process.env.REACT_APP_SOCIAL_GITHUB || 'https://github.com/clawstinger',
    process.env.REACT_APP_SOCIAL_DISCORD || 'https://discord.gg/clawstinger',
    process.env.REACT_APP_SOCIAL_LINKEDIN || '',
  ].filter(Boolean),
  
  // Default OG Image
  defaultOgImage: process.env.REACT_APP_OG_IMAGE || '/og-image.png',
  
  // Locale
  locale: process.env.REACT_APP_LOCALE || 'en_US',
  language: process.env.REACT_APP_LANGUAGE || 'en',
};

// Page-specific SEO configurations
export const PAGE_SEO = {
  home: {
    title: 'ClawStinger - Deploy OpenClaw AI Agents in 60 Seconds | Managed Claude & AI Hosting',
    description: 'Deploy OpenClaw, Claude bots, and AI assistants instantly. Managed hosting with RDP access, multi-agent support, and 99.9% uptime. Start your free trial today.',
    keywords: 'openclaw hosting, claude bot hosting, AI agent deployment, managed AI hosting, simpleclaw alternative, myclaw alternative, telegram AI bot, discord AI bot, claude opus hosting',
  },
  features: {
    title: 'Features - OpenClaw Hosting | RDP Access, Multi-Agent, Global Deployment',
    description: 'Explore ClawStinger features: 60-second deployment, RDP/SSH access, multi-agent support, 8 global regions, skills marketplace, and enterprise-grade security.',
    keywords: 'openclaw features, AI hosting features, RDP AI access, multi-agent AI, global AI deployment, AI skills marketplace',
  },
  pricing: {
    title: 'Pricing - Affordable OpenClaw & AI Agent Hosting Plans',
    description: 'Simple, transparent pricing for OpenClaw hosting. Start free, scale as needed. Plans from $29/month with multi-agent support and daily backups.',
    keywords: 'openclaw pricing, AI hosting cost, claude hosting price, cheap AI hosting, AI agent pricing',
  },
  about: {
    title: 'About ClawStinger - The Team Behind Managed OpenClaw Hosting',
    description: 'Learn about ClawStinger, our mission to make AI assistants accessible to everyone, and our team of developers passionate about AI infrastructure.',
    keywords: 'about clawstinger, openclaw company, AI hosting team, managed AI company',
  },
  contact: {
    title: 'Contact Us - ClawStinger Support & Sales',
    description: 'Get in touch with ClawStinger. Contact our support team for help with OpenClaw deployment, billing questions, or enterprise inquiries.',
    keywords: 'contact clawstinger, openclaw support, AI hosting help, clawstinger email',
  },
  docs: {
    title: 'Documentation - OpenClaw Deployment Guides & API Reference',
    description: 'Complete documentation for ClawStinger. Learn how to deploy OpenClaw, configure Claude models, connect messaging platforms, and manage your AI agents.',
    keywords: 'openclaw documentation, AI hosting docs, claude setup guide, telegram bot tutorial, discord bot guide',
  },
  blog: {
    title: 'Blog - AI Hosting News, Tutorials & Updates | ClawStinger',
    description: 'Stay updated with the latest in AI hosting. Tutorials, product updates, and insights on OpenClaw, Claude, and AI agent deployment.',
    keywords: 'openclaw blog, AI hosting news, claude tutorials, AI agent updates',
  },
  signin: {
    title: 'Sign In - ClawStinger Dashboard',
    description: 'Sign in to your ClawStinger account to manage your OpenClaw instances, view billing, and configure your AI agents.',
    keywords: 'clawstinger login, openclaw signin, AI hosting login',
  },
  signup: {
    title: 'Sign Up - Start Your Free OpenClaw Hosting Trial',
    description: 'Create your ClawStinger account and deploy your first OpenClaw AI agent in under 60 seconds. No credit card required for free trial.',
    keywords: 'clawstinger signup, openclaw free trial, AI hosting registration, start AI hosting',
  },
  terms: {
    title: 'Terms of Service - ClawStinger',
    description: 'Read ClawStinger terms of service for OpenClaw hosting, including acceptable use policy, payment terms, and service level agreement.',
    keywords: 'clawstinger terms, openclaw terms of service, AI hosting terms',
  },
  privacy: {
    title: 'Privacy Policy - ClawStinger',
    description: 'ClawStinger privacy policy. Learn how we protect your data, handle instance information, and comply with GDPR regulations.',
    keywords: 'clawstinger privacy, openclaw privacy policy, AI hosting data protection',
  },
  refund: {
    title: 'Refund & Cancellation Policy - ClawStinger',
    description: 'ClawStinger refund and cancellation policy. 7-day refund window, easy subscription cancellation, and transparent credit policies.',
    keywords: 'clawstinger refund, openclaw cancellation, AI hosting refund policy',
  },
  shipping: {
    title: 'Shipping & Delivery Policy - ClawStinger',
    description: 'ClawStinger delivery policy for digital services. Instant provisioning, global deployment regions, and 24/7 service availability.',
    keywords: 'clawstinger delivery, openclaw service delivery, AI hosting activation',
  },
  status: {
    title: 'System Status - ClawStinger Service Health',
    description: 'Check ClawStinger system status. Real-time uptime monitoring for all services, API endpoints, and global deployment regions.',
    keywords: 'clawstinger status, openclaw uptime, AI hosting status page',
  },
};

export default SEO_CONFIG;
